import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useServices } from "../useHook";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import ServicesCategoryModal from "../../../Components/Modals/ServicesCategoryModal/ServicesCategoryModal";
import { MdDelete } from "react-icons/md";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function ServiceCategories() {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.ServiceCategories.CREATE);
  const canUpdate = hasPermission(SystemPermissions.ServiceCategories.UPDATE);
  const canDelete = hasPermission(SystemPermissions.ServiceCategories.DELETE);
  const canShowActions = canUpdate || canDelete;

  const [modalOpen, setModalOpen] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [searchFilter, setSearchFilter] = useState("");

  const {
    addServiceCategory,
    getServicesCategories,
    updateServiceCategory,
    deleteServiceCategory,
    searchServicesCategories,
  } = useServices();

  const toggleModal = () => setModalOpen((prev) => !prev);

  const fetchCategories = async (currentOffset = offset) => {
    setIsLoading(true);
    try {
      await getServicesCategories(
        setServiceCategories,
        setTotal,
        currentOffset,
        limit,
        searchFilter,
      );
    } catch (error) {
      console.error("Error fetching services categories:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load services categories. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const handleSaveCategory = async (categoryData, id = null) => {
    setIsSubmitting(true);
    try {
      let response;
      if (id) response = await updateServiceCategory(id, categoryData);
      else response = await addServiceCategory(categoryData);

      if (response) {
        await fetchCategories(offset);
        toggleModal();
        Swal.fire("Success", "Category saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    toggleModal();
  };

  const handleDeleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      await deleteServiceCategory(id);
      await fetchCategories(offset);
      Swal.fire("Deleted!", "Category deleted successfully.", "success");
    } catch (error) {
      console.error("Error in handleDeleteCategory:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to delete category.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const response = await searchServicesCategories(searchFilter, 0, limit);
      setServiceCategories(response.data ?? []);
      setTotal(response.totalCount ?? 0);
      setOffset(0);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setOffset(0);
    fetchCategories(0);
  };

  if (isLoading) return <Loader message="Loading Services Categories..." />;

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Services Categories</h2>

        {canCreate && (
          <button
            onClick={() => {
              setCurrentCategory(null);
              toggleModal();
            }}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Category
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search categories"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th text-center">Image</th>
                <th className="table-th text-left">Name</th>
                <th className="table-th text-left">Description</th>
                {canShowActions && <th className="table-th text-center">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Array.isArray(serviceCategories) &&
              serviceCategories?.length > 0 ? (
                serviceCategories.map((category, index) => (
                  <tr
                    key={category.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td text-center">
                      {offset + index + 1}
                    </td>
                    <td className="table-td text-center">
                      <div className="">
                        {category.image ? (
                          <img
                            src={
                              category.image.startsWith("http")
                                ? category.image
                                : `${(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "")}${category.image.startsWith("/") ? "" : "/"}${category.image}`
                            }
                            alt={category.name}
                            className="h-14 w-14 object-cover rounded-xl shadow-md border-2 border-white ring-1 ring-gray-100  hover:scale-110 transition-transform duration-200"
                          />
                        ) : (
                          <div className="h-14 w-14 bg-gray-100 rounded-xl flex items-center justify-center text-[10px] text-gray-400 border-2 border-dashed border-gray-200 uppercase font-bold tracking-tighter">
                            No Img
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="text-gray-900 font-semibold text-base">
                        {category.name}
                      </div>
                    </td>
                    <td className="table-td overflow-hidden">
                      <div className="text-gray-600 text-sm line-clamp-2 max-w-md">
                        {category.description ? category.description : "—"}
                      </div>
                    </td>
                    {canShowActions && (
                      <td className="table-td">
                        <div className="flex items-center justify-center gap-2">
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() => handleEditCategory(category)}
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canShowActions ? 5 : 4} className="text-center py-16">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                        <FiFilter size={32} />
                      </div>
                      <p className="text-lg font-medium text-gray-500">
                        No service categories found
                      </p>
                      <p className="text-sm">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={(newOffset) => {
              setOffset(newOffset);
            }}
            className="bg-white"
          />
        </div>
      )}

      <ServicesCategoryModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        category={currentCategory}
        handleSaveCategory={handleSaveCategory}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export default ServiceCategories;
